import { FC, useCallback, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { Draggable } from 'react-beautiful-dnd';
import { dayjs } from 'utils/providers/Timezone';
import { TbDotsVertical } from 'react-icons/tb';
import { compact } from 'lodash';
import { NavAction } from 'ui/components/nav';
import { DeleteItemModal } from 'ui/components/common';
import { useGetSubscription } from 'modules/account/settings/hooks';
import c, { SIZE } from 'utils/constants';
import useModal from 'ui/partials/Modal/useModal';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import { SimpleLead } from 'utils/types';
import { AvatarGroup } from 'ui/components/img/Avatar';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import { Checkbox } from 'ui/components';

import { useDeleteDeal } from '../hooks';
import { useDealsContext } from '../containers/Deals/Deals';
import _c from '../utils/constants';
import DealMoveModal from './DealMoveModal';

const { ACTIONS, PERMISSION_KEYS } = c;

type PROPS = {
  index: number;
  id: string;
  name: string;
  createdAt: string;
  amount: string;
  company?: string;
  isDone?: boolean;
  contacts?: string[] | SimpleLead[];
  checked?: boolean;
  onCheck?: (id: string) => void;
};

const DraggableCard: FC<PROPS> = ({
  index,
  id,
  name,
  createdAt,
  amount,
  company = null,
  isDone = false,
  contacts = [],
  checked,
  onCheck,
}) => {
  const navigate = useNavigate();

  const canEdit = useCanRolePermissions(ACTIONS.UPDATE, PERMISSION_KEYS.DEALS);
  const canDelete = useCanRolePermissions(
    ACTIONS.DELETE,
    PERMISSION_KEYS.DEALS,
  );

  const { data: subscription } = useGetSubscription();
  const { setSelectedDealId, selectedDealId, pipelineId } = useDealsContext();
  const { showModal } = useModal();

  const { mutate: deleteDeal, isLoading: deleteLoading } = useDeleteDeal();

  const handleDeleteDeal = useCallback(() => {
    deleteDeal({ id, pipelineId });
  }, [deleteDeal, id, pipelineId]);

  const navOptions = useMemo(() => {
    const opts = [
      {
        text: 'View',
        key: 'view',
        onClick: () => {
          setSelectedDealId?.(id);
        },
      },
      canEdit &&
        !subscription?.isExpired && {
          text: 'Move',
          key: 'move',
          onClick: () => {
            showModal({
              size: SIZE.Small,
              title: 'Deal',
              content: <DealMoveModal dealId={id} />,
            });
          },
        },
      canDelete &&
        !subscription?.isExpired && {
          text: 'Delete',
          key: 'delete',
          onClick: () => {
            showModal({
              title: 'Deal',
              content: (
                <DeleteItemModal
                  text="deal"
                  onClickDelete={handleDeleteDeal}
                  isLoading={deleteLoading}
                  validation
                />
              ),
            });
          },
        },
    ];

    return compact(opts);
  }, [
    canDelete,
    canEdit,
    deleteLoading,
    handleDeleteDeal,
    id,
    setSelectedDealId,
    showModal,
    subscription?.isExpired,
  ]);

  return (
    <Draggable
      draggableId={id}
      index={index}
      isDragDisabled={subscription?.isExpired || !canEdit}
    >
      {(provided, snapshot) => (
        <div
          data-tut={TOUR_SELECTOR.TOUR.SALES.DEAL.MANAGE.DEALS_CARD}
          className={cn(
            'shadow-leapify-sm relative',
            'mb-3 py-[14px] px-[18px]',
            'rounded-md border-2',
            'hover:border-secondary-base transition',
            !isDone
              ? 'bg-fill-tertiary'
              : 'border-separator-primary bg-[#F5FDFF] opacity-60',
            selectedDealId === id
              ? 'border-2 border-secondary-base'
              : 'border-transparent',
            (snapshot.isDragging || checked) &&
              'border-2 !border-secondary-base shadow',
          )}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          <div>
            <div
              className="absolute top-0 left-0 w-full h-full bg-none"
              onClick={() => setSelectedDealId?.(id)}
            />
            <div
              className={cn(
                'flex items-start justify-between truncate',
                'border-b border-separator-primary',
                'pb-[14px]',
              )}
            >
              <div className="flex gap-2">
                <div className="relative">
                  <Checkbox
                    checked={checked}
                    onChange={() => {
                      onCheck?.(id);
                    }}
                  />
                </div>
                <div>
                  <button
                    type="button"
                    className={cn(
                      'relative',
                      'text-lg font-semibold leading-[22px]',
                      'hover:text-secondary-dark hover:underline',
                    )}
                    onClick={() => navigate(`${id}?view=${_c.BOARD}`)}
                  >
                    {name}
                  </button>
                  <p className="text-xs text-content">
                    {dayjs(createdAt).format(c.DATE_FORMAT_V2)}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-[14px]">
              <p className="leading-5 text-xs">Company</p>
              <p className="text-sm font-semibold leading-[22px]">{company}</p>
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <p className="leading-5 text-xs">Contacts</p>
                  {!!contacts?.length && (
                    <AvatarGroup
                      size={SIZE.Small}
                      users={contacts}
                      className="mt-1.5"
                    />
                  )}
                </div>
                <p className="text-lg font-semibold text-[#1973E8]">
                  $ {amount}
                </p>
              </div>
            </div>
          </div>
          <div className="absolute top-2 right-2">
            <NavAction
              label={<TbDotsVertical className="h-5 w-5 text-secondary" />}
              options={navOptions}
              optionsClassName="w-[120px] right-0"
            />
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default DraggableCard;
